/**
 * v2.3 FDT-O-005 — celebration layer (audio + haptic + visual moments).
 *
 * Shared, dependency-light helpers for the play engine and result screens.
 * Everything degrades to a no-op when disabled / reduced-motion / muted / low
 * battery, so callers fire-and-forget. ZERO asset files: the success chord is
 * synthesized with Web Audio and the burst is inline DOM (UI-2 / I-20) — no
 * download, no PNG.
 *
 * Moment map (per OPTIMIZATION-v2.3 FDT-O-005 §Fix):
 *   1 connect  → light haptic + blip            (play-engine onProgress)
 *   2 complete → success haptic + successChord()  (play-engine win)
 *   3 replay   → silent stroke redraw            (play-engine replaySolvedPath)
 *   4 star     → tiered haptic + inkBurst()       (practice star reveal)
 *   5 streak   → medium haptic + counter bump     (G1, FDT-O-004)
 */
import { haptic } from '../platform/bridge';
import { profile } from '../state/profile';

/** Hard cap on burst particles so a slow device never drops frames (R-005d). */
export const MAX_PARTICLES = 24;

/** Clamp a requested particle count into [0, MAX_PARTICLES]. */
export function clampParticles(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.floor(n), MAX_PARTICLES);
}

/** Best-effort low-power flag. getBattery() is absent on most browsers → stays
 *  false (no degrade). When present, true once discharging below 20%. */
let lowPower = false;
(() => {
  const nav = navigator as Navigator & {
    getBattery?: () => Promise<{ level: number; charging: boolean }>;
  };
  nav.getBattery?.()
    .then((b) => {
      lowPower = !b.charging && b.level < 0.2;
    })
    .catch(() => {
      /* unsupported — leave lowPower false */
    });
})();

/**
 * Haptic gated by the user's Settings toggle (AC-6) + low-power degrade.
 * On low battery the minor buzzes drop but the big "success" stays.
 */
export function celebrateHaptic(
  kind: 'light' | 'medium' | 'success' | 'warning' | 'error',
): void {
  try {
    if (profile().get().hapticsEnabled === false) return;
  } catch {
    /* profile not yet initialized — allow */
  }
  if (lowPower && kind !== 'success') return;
  void haptic(kind);
}

/**
 * Moment 2 — success chord (C5-E5-G5 major triad), synthesized so there is no
 * asset file. Soft ~0.8s bell with a tiny arpeggio spread. No-op when sound is
 * disabled (caller passes `profile.soundEnabled !== false`).
 */
export function successChord(enabled: boolean): void {
  if (!enabled) return;
  try {
    const Ctor =
      (window as typeof window & { AudioContext?: typeof AudioContext }).AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const now = ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const freq = [523.25, 659.25, 783.99][i]!; // C5, E5, G5
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = now + i * 0.04;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.06, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.8);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.85);
    }
    setTimeout(() => void ctx.close().catch(() => {}), 1100);
  } catch {
    /* AudioContext can fail before a user gesture — ignore */
  }
}

export interface InkBurstOpts {
  count?: number;
  reduceMotion?: boolean;
  color?: string;
}

/**
 * Moment 4 — ink-splatter burst (NOT candy confetti): a handful of dark ink
 * dots flung outward + fading, echoing the one-stroke brush. Capped particle
 * count; no-op under reduced motion. The layer is position:fixed over the
 * host's screen rect (so it doesn't depend on host positioning) and removes
 * itself after the burst.
 */
export function inkBurst(host: HTMLElement, opts: InkBurstOpts = {}): void {
  if (opts.reduceMotion) return;
  const N = clampParticles(opts.count ?? 14);
  if (N === 0) return;
  const color = opts.color ?? 'var(--color-ink, #1A1A1A)';
  const rect = host.getBoundingClientRect();
  const layer = document.createElement('div');
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText =
    `position:fixed;left:${rect.left}px;top:${rect.top}px;` +
    `width:${rect.width}px;height:${rect.height}px;pointer-events:none;overflow:visible;z-index:50;`;
  const cx = rect.width / 2;
  const cy = rect.height * 0.42;
  for (let i = 0; i < N; i++) {
    const dot = document.createElement('div');
    const size = 4 + (i % 4) * 2;
    const ang = (i / N) * Math.PI * 2 + (i % 3) * 0.3;
    const dist = 60 + (i % 5) * 22;
    const dx = Math.cos(ang) * dist;
    const dy = Math.sin(ang) * dist - 20; // slight upward bias (a splash, not a fall)
    dot.style.cssText =
      `position:absolute;left:${cx}px;top:${cy}px;width:${size}px;height:${size}px;` +
      `border-radius:60% 40% 55% 45%;background:${color};opacity:0;` +
      `transform:translate(-50%,-50%) scale(0.4);will-change:transform,opacity;`;
    layer.appendChild(dot);
    // Web Animations API — guarded so non-browser test envs don't throw.
    dot.animate?.(
      [
        { transform: 'translate(-50%,-50%) translate(0,0) scale(0.4)', opacity: 0 },
        { opacity: 0.85, offset: 0.15 },
        { transform: `translate(-50%,-50%) translate(${dx}px,${dy}px) scale(1)`, opacity: 0 },
      ],
      { duration: 520 + (i % 6) * 60, easing: 'cubic-bezier(0.2,0.7,0.3,1)', fill: 'forwards' },
    );
  }
  host.appendChild(layer);
  setTimeout(() => layer.remove(), 950);
}
